import '../../App.css';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl, { LngLatBounds } from 'mapbox-gl';
import {
  appSettingsLocalStorageKey,
  defaultLat,
  defaultLng,
  gaTrackingId,
  mapboxToken,
  writeToLog,
} from '../../const';
import { createTamagui, TamaguiProvider } from 'tamagui';
import config from '@shane-collective/design-system/dist/themes/tamagui.config';
import 'animate.css';
// import {
//   checkPermissions,
//   requestLocation,
// } from "../../loaders/LocationPermissionsLoader";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppSettingsDialog } from '../../components/settings/SettingsDialog';
import { HelpLinksDialog } from '../../components/help/HelpDialog';
// import { useDebouncedHandleMove } from "../../hooks/UseDebouncedHandleMove";
import { usePopUp } from '../../hooks/UsePopUp';
// import { useLoadMoreResultsOnZoom } from "../../hooks/UseLoadMoreResultsOnZoom";
import { useLongPressMapInteraction } from '../../hooks/UseLongPressMapInteraction';
import { useSearch } from '../../hooks/UseSearch';
import useMapInitialization from '../../hooks/UseMapInitialization';
// import SearchingOverlay from "../../components/searchingOverlay/SearchingOverlay";
import MapContainer from '../../components/mapContainer/MapContainer';
import SearchThisAreaDesktop from '../../components/searchThisArea/SearchThisAreaDesktop';
import { useMapFunctions } from '../../hooks/UseMapFunctions';
import { useAppState } from '../../core/appState/UseAppStateZustand';
import ReactGA from 'react-ga4';
import { getToken } from '../../core/api/getToken';
import { fetchListByUuid, fetchSearch, preloadMap } from '../../client';
import { useShaneClientContext } from '../../core/shaneClient/ShaneClientProvider';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { debounce, isArray } from 'lodash';
import { CustomMarker } from '../../types/types';
import { calculateMaxVisibleForZoom } from '../../functions/Functions';
import { moveBottomRightControls } from '../../loaders/MapControlsLoader';

const TRACKING_ID = gaTrackingId;

mapboxgl.accessToken = mapboxToken;
const tamaguiConfig = createTamagui(config);

const App = memo(function App() {
  const { shaneClient } = useShaneClientContext();

  useEffect(() => {
    //@ts-ignore
    writeToLog('environment:' + process.env.REACT_APP_ENVIRONMENT);

    //@ts-ignore
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      writeToLog(
        '[GA4] Not sending events to Google Analytics while in development environment',
      );
      return;
    }

    if (ReactGA.isInitialized) return;

    ReactGA.initialize(TRACKING_ID);
    ReactGA.send('pageview');
    writeToLog('[GA4] initialised GA4 and sent pageview event');
  }, []);

  //SECTION - set up state, refs and setState and explode those required in this file

  const accessToken = useAppState(useShallow((s) => s.accessToken));
  const isMapInit = useAppState(useShallow((s) => s.isMapInit));

  const {
    setLng,
    setLat,
    setIsSettingsDialogOpen,
    setIsHelpDialogOpen,
    // setFlyingTo,
    setShowRatingMarkers,
    setAccessToken,
    setAccessTokenExp,
  } = useAppState.getState();

  useEffect(() => {
    if (accessToken) return;

    const debounceDelay = 300; // ms

    const timeout = setTimeout(() => {
      const refreshToken = async () => {
        const data = await getToken();

        if (data) {
          setAccessToken(data.token);
          setAccessTokenExp(data.exp);

          const now = new Date();
          const timeUntilExpiration =
            new Date(data.exp).getTime() - now.getTime();

          if (timeUntilExpiration <= 0) {
            data.token = await refreshToken();
            writeToLog('Token refreshed immediately!');
          } else {
            writeToLog(
              `Token expires in ${
                timeUntilExpiration / 1000
              } seconds. Waiting for expiration...`,
            );

            setTimeout(async () => {
              await refreshToken();
            }, timeUntilExpiration);
          }
        }
      };

      refreshToken();
    }, debounceDelay);

    return () => clearTimeout(timeout);
  }, [setAccessToken, setAccessTokenExp, accessToken]);

  const location = useLocation();

  const popupContainerRef = useRef<any>(null);
  const popup = useRef(
    new mapboxgl.Popup({ offset: 15, closeOnClick: false }),
  ).current;
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const namePopup = useRef(
    new mapboxgl.Popup({ offset: 15, closeOnClick: false, closeButton: false }),
  ).current;

  const hasDataInit = useRef(false);
  const [showSTA, setShowSTA] = useState<boolean>(false);
  const searchBounds = useRef<LngLatBounds | null>(null);

  const lastViewport = useRef({ lng: 0, lat: 0, zoom: 0 });

  //SECTION - set up map and Shane component interactions, e.g. searching, rating, creating locations
  const SearchHandlers = useSearch({
    isSmallScreen: true,
    popup,
    popupContainerRef,
    map,
    mapContainer,
    namePopup,
  });
  const { addLocationsToMap, updateMapWithSearchResults } = useMemo(
    () => SearchHandlers,
    [SearchHandlers],
  );

  const mapFunctionsHandlers = useMapFunctions();
  const { searchResultsToGeoJson, setMarkerVisibility } = useMemo(
    () => mapFunctionsHandlers,
    [mapFunctionsHandlers],
  );

  const selectedFeature = useRef<any>(null);

  useEffect(() => {
    setLat(defaultLat);
    setLng(defaultLng);
  }, [setLat, setLng]);

  // Initialize outside of component or useEffect
  const currentlyVisibleMarkers = useRef<
    Record<string, { id: string; marker: mapboxgl.Marker }>
  >({});

  //@ts-ignore
  const debounceId = useRef<NodeJS.Timeout | null>(null);
  const newMarkersRef = useRef<CustomMarker[]>([]);
  const preloadedGeoJson = useAppState(useShallow((s) => s.preloadedGeoJson));
  const [isListMode, setIsListMode] = useState<boolean>(false);
  const [isPreloadMode, setIsPreloadMode] = useState<boolean>(false);
  const [isLoadCompleted, setIsLoadCompleted] = useState<boolean>(false);
  const geoJsonRef = useRef<any>(null);
  const listModeRef = useRef<boolean>(isListMode);

  useEffect(() => {
    listModeRef.current = isListMode;
  }, [isListMode]);

  const moveEndHandler = useCallback(async () => {
    if (!map.current || newMarkersRef.current.length === 0) return;
    if (listModeRef.current) return;

    if (map.current.getZoom() < 7.99) {
      const features = map.current.queryRenderedFeatures({
        layers: ['clusters'],
      });

      // Filter only clusters
      const clusters = features.filter(
        (f) => f.properties && f.properties.cluster === true,
      );

      if (clusters.length > 0) {
        // only add markers which are not in a cluster
        const bounds = map.current.getBounds();
        if (!bounds) return;

        const zoom = map.current.getZoom();
        const maxVisible = calculateMaxVisibleForZoom(zoom);

        const featuresInView = newMarkersRef.current.filter(({ marker }) =>
          bounds.contains(marker.getLngLat()),
        );
        const visible = featuresInView.slice(0, maxVisible);
        const toShowIds = new Set(visible.map((m) => m.id));

        const clusteredMarkers = features.filter(
          (f) => f.properties && f.properties.cluster,
        );

        async function processClusterLeaves() {
          // Wrap your logic in an async function
          var markersToNotAdd: string[] = [];

          // Create an array to hold all the Promises from getClusterLeaves calls
          const leavePromises: Promise<any[]>[] = [];

          clusteredMarkers.forEach((cluster) => {
            const clusterId = cluster?.properties?.cluster_id;
            const source = map?.current?.getSource('markers');

            if (source) {
              // Convert the callback-based getClusterLeaves into a Promise
              const promise = new Promise<any[]>((resolve, reject) => {
                //@ts-ignore (Assuming this is still needed)
                source.getClusterLeaves(
                  clusterId,
                  Infinity,
                  0,
                  (err: any, leaves: any) => {
                    if (err) {
                      console.error('Error getting cluster leaves:', err);
                      reject(err); // Reject the promise on error
                      return;
                    }
                    resolve(leaves || []); // Resolve with leaves or an empty array if null/undefined
                  },
                );
              });
              leavePromises.push(promise);
            }
          });

          // Wait for all promises to resolve
          try {
            const allLeaves = await Promise.all(leavePromises);

            // Process all leaves after all promises have resolved
            allLeaves.forEach((leavesArray) => {
              for (const l of leavesArray) {
                markersToNotAdd.push(l.properties.id.toString());
              }
            });

            if (!map.current) return;

            currentlyVisibleMarkers.current = {};

            // get all the currentlyvisiblemarkers which are not in markerstonotadd and add them
            for (const m of newMarkersRef.current) {
              if (
                markersToNotAdd.includes(m.id.toString()) ||
                !toShowIds.has(m.id)
              ) {
                m.marker.remove();
              } else {
                const marker = m.marker;
                marker.addTo(map.current);
                currentlyVisibleMarkers.current[m.id.toString()] = {
                  id: m.id.toString(),
                  marker,
                };
              }
            }
          } catch (error) {
            console.error('One or more cluster leaf operations failed:', error);
          }
        }

        // Call the async function to start the process
        // You would call this function where your original logic was placed
        processClusterLeaves();

        return;
      }
      // else {
      //   // set STA flag
      //   if (
      //     Object.keys(currentlyVisibleMarkers.current).length === 0 &&
      //     clusters.length === 0
      //   ) {
      //     setShowSTA(true);
      //   } else {
      //     setShowSTA(false);
      //   }
      // }
    }

    const bounds = map.current.getBounds();
    if (!bounds) return;

    const zoom = map.current.getZoom();
    const maxVisible = calculateMaxVisibleForZoom(zoom);

    const featuresInView = newMarkersRef.current.filter(({ marker }) =>
      bounds.contains(marker.getLngLat()),
    );
    const visible = featuresInView.slice(0, maxVisible);
    const toShowIds = new Set(visible.map((m) => m.id));

    // Remove markers not in view
    for (const id of Object.keys(currentlyVisibleMarkers.current)) {
      if (!toShowIds.has(id)) {
        currentlyVisibleMarkers.current[id].marker.remove();
        delete currentlyVisibleMarkers.current[id];
      }
    }

    // Add new visible markers
    for (const { id, marker } of visible) {
      if (!currentlyVisibleMarkers.current[id]) {
        marker.addTo(map.current);
        currentlyVisibleMarkers.current[id] = { id: id.toString(), marker };
      }
    }

    // const { setMarkersRef } = useAppState.getState();
    // setMarkersRef(newMarkersRef.current);

    if (!isLoadCompleted) setIsLoadCompleted(true);
  }, [isLoadCompleted]);

  const handleMove = useCallback(() => {
    if (!map?.current) return;

    moveEndHandler();

    const center = map.current.getCenter();
    const zoomLevel = map.current.getZoom();
    const newBounds = map.current.getBounds();

    if (
      searchBounds.current &&
      newBounds &&
      searchBounds.current !== newBounds
    ) {
      // check if new bounds are completely within original bounds
      // const isLngContained =
      //   newBounds.getWest() >= searchBounds.current.getWest() &&
      //   newBounds.getEast() <= searchBounds.current.getEast();

      // const isLatContained =
      //   newBounds.getSouth() >= searchBounds.current.getSouth() &&
      //   newBounds.getNorth() <= searchBounds.current.getNorth();

      // // Both longitude and latitude must be contained
      // if (isLngContained && isLatContained) {
      //   setShowSTA(false);
      // } else {
      setShowSTA(true);
      // }
    } else {
      setShowSTA(false);
    }

    let newViewport: any = {
      lng: parseFloat(center.lng.toFixed(4)),
      lat: parseFloat(center.lat.toFixed(4)),
      zoom: parseFloat(zoomLevel.toFixed(2)),
    };

    const threshold = 0.0001;
    const changed =
      Math.abs(newViewport.lng - lastViewport.current.lng) > threshold ||
      Math.abs(newViewport.lat - lastViewport.current.lat) > threshold ||
      Math.abs(newViewport.zoom - lastViewport.current.zoom) > 0.01;

    if (!changed) return;

    const bounds = map.current.getBounds();

    if (bounds) {
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();

      newViewport = {
        ...newViewport,
        minLat: southWest.lat,
        minLng: southWest.lng,
        maxLat: northEast.lat,
        maxLng: northEast.lng,
      };
    }

    lastViewport.current = newViewport;

    try {
      // @ts-ignore
      window.flutter_inappwebview.callHandler(
        'fromWebApp',
        'viewport_change:' + JSON.stringify(newViewport),
      );
    } catch (e) {}
  }, [map, moveEndHandler]);



  const debouncedHandleMoveEnd = debounce(() => {
    handleMove();
  }, 150);
  useEffect(() => {
    if (!preloadedGeoJson || !map.current) return;

    const mapInstance = map.current;

    const preloadData = async () => {
      if ((newMarkersRef?.current ?? []).length === 0) {
        const newMarkers =
          (await addLocationsToMap(preloadedGeoJson, true, true, true)) ?? [];

        // add listeners to markers
        newMarkers?.forEach((marker) => {
          setMarkerVisibility(marker, true);
          const feature = preloadedGeoJson.features.find(
            (r: any) => r.properties.id === marker.id,
          );

          const onMarkerClick = (e: any) => {
            e.stopPropagation();
            if (map.current) {
              // remove popup from other location if it exists
              if (
                selectedFeature?.current &&
                selectedFeature.current !== feature
              ) {
                const marker = newMarkersRef.current?.find(
                  (m: any) => m.id === selectedFeature.current.properties.id,
                )?.marker;

                if (marker) {
                  marker.getPopup()?.remove();
                }
                selectedFeature.current = null;
              }

              const newPopup = new mapboxgl.Popup({ offset: 25 });

              newPopup
                //@ts-ignore
                .setLngLat(feature.geometry.coordinates)
                .setHTML(
                  '<h3 style="font-family: Rethink Sans;">' +
                    feature?.properties?.name +
                    '</h3>',
                )
                .addTo(map.current);

              marker.marker.setPopup(newPopup);

              if (selectedFeature?.current === feature) {
                // send message to ff to open location dialog
                try {
                  if (feature?.properties?.externalId) {
                    //@ts-ignore
                    window.flutter_inappwebview.callHandler(
                      'fromWebApp',
                      'open_ext_location ' + feature?.properties?.externalId,
                    );
                  } else if (feature?.properties?.rrId) {
                    //@ts-ignore
                    window.flutter_inappwebview.callHandler(
                      'fromWebApp',
                      'open_rr_location:' +
                        JSON.stringify({
                          id: feature?.properties?.rrId,
                          lat: feature?.properties.lat,
                          lng: feature?.properties.lng,
                        }),
                    );
                  } else if (typeof feature?.properties?.id === 'string') {
                    //@ts-ignore
                    window.flutter_inappwebview.callHandler(
                      'fromWebApp',
                      'open_mapbox_location ' + feature?.properties?.id,
                    );
                  } else {
                    //@ts-ignore
                    window.flutter_inappwebview.callHandler(
                      'fromWebApp',
                      'open_location ' + feature?.properties?.id,
                    );
                  }
                } catch (e) {
                  // console.log("caz POPPING 2", feature?.properties);
                }
              }

              selectedFeature.current = feature;
            }
          };

          const el = marker.marker.getElement();

          el.addEventListener('click', onMarkerClick);

          const cleanupListeners = () => {
            el.removeEventListener('click', onMarkerClick);
          };

          marker.marker.on('remove', () => {
            cleanupListeners();
          });
        });

        newMarkersRef.current = newMarkers;
      }

      // Clear previous debounce
      if (debounceId.current) clearTimeout(debounceId.current);

      debounceId.current = setTimeout(() => {
        mapInstance.on('moveend', debouncedHandleMoveEnd);
      }, 600);
    };

    preloadData();

    return () => {
      if (debounceId.current) {
        clearTimeout(debounceId.current);
        debounceId.current = null;
      }

      mapInstance.off('moveend', debouncedHandleMoveEnd);
    };
  }, [preloadedGeoJson, addLocationsToMap, debouncedHandleMoveEnd, setMarkerVisibility]);

  const listIdRef = useRef<string | null>(null);
  const searchIdRef = useRef<string | null>(null);
  const locationIdRef = useRef<string | null>(null);
  // const viewportZoomRef = useRef<number | null>(null);
  // const viewportLatRef = useRef<number | null>(null);
  // const viewportLngRef = useRef<number | null>(null);
  const skipLoadAnimationRef = useRef<boolean>(false);
  const usrLatRef = useRef<number | null>(null);
  const usrLngRef = useRef<number | null>(null);
  const isReadOnlyRef = useRef<boolean>(false);

  const applyParamChanges = useCallback(
    (
      listId: string | null,
      searchId: string | null,
      locId: string | null,
      userLat: string | null,
      userLng: string | null,
      skipLoadAnimation: boolean,
      isInit: boolean,
      skipBoundsChange: boolean,
    ) => {
      try {
        //@ts-ignore
        window.flutter_inappwebview.callHandler(
          'fromWebApp',
          'running applyParamChanges ' +
            JSON.stringify({
              listId,
              listIdRef: listIdRef.current,
              locId,
              locationIdRef: locationIdRef.current,
              userLat,
              userLatRef: usrLatRef.current,
              userLng,
              usrLngRef: usrLngRef.current,
              skipLoadAnimation,
              skipLoadAnimationRef: skipLoadAnimationRef.current,
              isInit,
              searchId,
              searchIdRef: searchIdRef.current,
              skipBoundsChange,
            }),
        );
      } catch (e) {
        // console.log(
        //   "caz",
        //   JSON.stringify({
        //     listId,
        //     listIdRef: listIdRef.current,
        //     locId,
        //     locationIdRef: locationIdRef.current,
        //     userLat,
        //     userLatRef: usrLatRef.current,
        //     userLng,
        //     usrLngRef: usrLngRef.current,
        //     skipLoadAnimation,
        //     skipLoadAnimationRef: skipLoadAnimationRef.current,
        //     isInit,
        //   })
        // );
      }
      // if userLat + userLng, set userMarker
      if (
        //isInit &&
        userLat &&
        (usrLatRef.current ?? -999).toString() !== userLat &&
        (usrLngRef.current ?? -999).toString() !== userLng &&
        userLng &&
        map.current
      ) {
        console.log('setting user marker');
        const el = document.createElement('div');
        el.className = 'marker-user';

        new mapboxgl.Marker(el)
          .setLngLat([parseFloat(userLng), parseFloat(userLat)])
          .addTo(map.current);
      }

      if (listId && listId !== listIdRef.current) {
        console.log('setting list mode');
        setIsListMode(true);
      }

      handleMove();

      if (searchId && searchId !== searchIdRef.current) {
        console.log('running search');
        setIsPreloadMode(false);
        setShowSTA(false);

        if (searchIdRef.current !== null) {
          for (const m of newMarkersRef.current) {
            (m as any).marker.remove();
          }
          newMarkersRef.current = [];
          geoJsonRef.current = null;

          if (!!map?.current) {
            //@ts-ignore
            map.current.getSource('markers').setData({
              type: 'FeatureCollection',
              features: [], // An empty array of features
            });
          }
        }

        const fetchData = async () => {
          if (!map?.current) return;

          try {
            const { data } = await fetchSearch({
              client: shaneClient,
              path: { searchId },
              headers: { Authorization: 'Bearer ' + accessToken },
            });

            try {
              //@ts-ignore
              window.flutter_inappwebview.callHandler(
                'fromWebApp',
                '***FETCHING SEARCH RESULTS*** ' +
                  JSON.stringify({ data, searchId, skipBoundsChange }),
              );
            } catch (e) {}

            if (!data) return;

            //@ts-ignore
            let results = JSON.parse(data);

            if (!isArray(results)) {
              results = results.data;
            }

            //@ts-ignore
            const geoJson = searchResultsToGeoJson(results);
            geoJsonRef.current = geoJson;
            const markers = await addLocationsToMap(geoJson, true, true, true);

            const bounds = new mapboxgl.LngLatBounds();

            markers?.forEach((marker) => {
              bounds.extend(marker.marker.getLngLat());
              setMarkerVisibility(marker, true);
              const feature = geoJson.features.find(
                (r: any) => r.properties.id === marker.id,
              );

              const onMarkerClick = (e: any) => {
                e.stopPropagation();
                if (map.current) {
                  if (
                    selectedFeature?.current &&
                    selectedFeature.current !== feature
                  ) {
                    const marker = markers?.find(
                      (m: any) =>
                        m.id === selectedFeature.current.properties.id,
                    )?.marker;

                    if (marker) {
                      marker.getPopup()?.remove();
                    }

                    selectedFeature.current = null;
                  }

                  const newPopup = new mapboxgl.Popup({ offset: 25 });

                  newPopup
                    //@ts-ignore
                    .setLngLat(feature.geometry.coordinates)
                    .setHTML(
                      '<h3 style="font-family: Rethink Sans;">' +
                        feature?.properties?.name +
                        '</h3>',
                    )
                    .addTo(map.current);

                  marker.marker.setPopup(newPopup);

                  if (selectedFeature.current === feature) {
                    try {
                      if (feature?.properties?.externalId) {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_ext_location ' +
                            feature?.properties?.externalId,
                        );
                      } else if (feature?.properties?.rrId) {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_rr_location:' +
                            JSON.stringify({
                              id: feature?.properties?.rrId,
                              lat: feature?.properties.lat,
                              lng: feature?.properties.lng,
                            }),
                        );
                      } else if (typeof feature?.properties?.id === 'string') {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_mapbox_location ' + feature?.properties?.id,
                        );
                      } else {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_location ' + feature?.properties?.id,
                        );
                      }
                    } catch (e) {
                      // console.log("caz POPPING 3", feature?.properties);
                    }
                  }
                  selectedFeature.current = feature;
                }
              };

              const el = marker.marker.getElement();

              el.addEventListener('click', onMarkerClick);

              const cleanupListeners = () => {
                el.removeEventListener('click', onMarkerClick);
              };

              marker.marker.on('remove', () => {
                cleanupListeners();
              });
            });

            newMarkersRef.current = markers ?? [];

            if (!map?.current) return;

            if (!skipBoundsChange) {
              try {
                //@ts-ignore
                window.flutter_inappwebview.callHandler(
                  'fromWebApp',
                  '***SEARCH RESULTS - FITTING BOUNDS*** ',
                );
              } catch (e) {}

              // if the current viewport overlaps with the new bounds at all, do a setcenter rather than fitbounds
              function checkOverlap(
                bounds1: mapboxgl.LngLatBounds,
                bounds2: mapboxgl.LngLatBounds,
              ): boolean {
                // Extract coordinates for clarity
                const minLon1 = bounds1.getWest();
                const maxLon1 = bounds1.getEast();
                const minLat1 = bounds1.getSouth();
                const maxLat1 = bounds1.getNorth();

                const minLon2 = bounds2.getWest();
                const maxLon2 = bounds2.getEast();
                const minLat2 = bounds2.getSouth();
                const maxLat2 = bounds2.getNorth();

                console.log(minLon1, maxLon1, minLat1, maxLat1);
                console.log(minLon2, maxLon2, minLat2, maxLat2);

                // Condition for NO overlap:
                // If one box is entirely to the left of the other, OR
                // If one box is entirely to the right of the other, OR
                // If one box is entirely below the other, OR
                // If one box is entirely above the other.
                const noOverlap =
                  maxLon1 < minLon2 || // bounds1 is entirely to the left of bounds2
                  minLon1 > maxLon2 || // bounds1 is entirely to the right of bounds2
                  maxLat1 < minLat2 || // bounds1 is entirely below bounds2
                  minLat1 > maxLat2; // bounds1 is entirely above bounds2

                // If there is no overlap, then they do not overlap.
                // Otherwise, they must overlap.
                return !noOverlap;
              }

              //@ts-ignore
              if (checkOverlap(map.current.getBounds(), bounds)) {
                map.current.easeTo({
                  center: bounds.getCenter(),
                  duration: 400,
                  //@ts-ignore
                  easing: function (t) {
                    return t; // Linear easing
                  },
                });
              } else {
                map.current.fitBounds(bounds, {
                  padding: 50,
                  maxZoom: 15,
                  minZoom: 4,
                  animate: true, //!skipLoadAnimation,
                  screenSpeed: 1.5,
                  //linear:true,
                  easing: (t) => {
                    return 1 - Math.pow(1 - t, 5);
                  },
                  essential: true,
                });
              }
            } else {
              if (map?.current) {
                try {
                  //@ts-ignore
                  window.flutter_inappwebview.callHandler(
                    'fromWebApp',
                    '***SEARCH RESULTS - SETTING CENTER*** ',
                  );
                } catch (e) {}
                map.current.setCenter(map.current.getCenter()); // Triggers move
              }
            }

            map.current.once('idle', () => {
              setIsLoadCompleted(true);
              if (map.current)
                // save bounds from search (for STA check)
                searchBounds.current = map.current.getBounds();
            });
          } catch (error) {
            console.error('Error fetching data:', error);
          }
        };

        fetchData();
      } else if (
        (!searchId || searchId === '') &&
        searchId !== searchIdRef.current &&
        // !isInit &&
        searchIdRef.current !== null
      ) {
        console.log('clearing search');
        // clear search
        try {
          try {
            //@ts-ignore
            window.flutter_inappwebview.callHandler(
              'fromWebApp',
              'clearing search results ',
            );
          } catch (e) {}
          for (const m of newMarkersRef.current) {
            (m as any).marker.remove();
          }
          newMarkersRef.current = [];
          geoJsonRef.current = null;
          searchIdRef.current = null;

          if (!!map?.current) {
            //@ts-ignore
            map.current.getSource('markers').setData({
              type: 'FeatureCollection',
              features: [], // An empty array of features
            });
          }

          setShowSTA(false);
          isInit = true;
        } catch (e) {
          try {
            //@ts-ignore
            window.flutter_inappwebview.callHandler(
              'fromWebApp',
              'clearing search results error' + JSON.stringify(e),
            );
          } catch (e) {}
        }
      } else if (listId && listId !== listIdRef.current) {
        // console.log("setting list mode");
        // setIsListMode(true);

        try {
          //@ts-ignore
          window.flutter_inappwebview.callHandler(
            'fromWebApp',
            'getting list results ',
          );
        } catch (e) {}

        const fetchListData = async () => {
          if (!map?.current) return;

          try {
            console.log('getting list data');
            const { data } = await fetchListByUuid({
              client: shaneClient,
              path: { listUuid: listId },
              headers: { Authorization: 'Bearer ' + accessToken },
            });

            if (!data) return;

            //@ts-ignore
            const results = data?.locations;

            console.log('creating geojson');
            const geoJson = searchResultsToGeoJson(
              //@ts-ignore
              results?.map((r: any) => {
                return {
                  ...r,
                  locationName: r.name,
                  locationAddress: r.address,
                  locationCategory: r.category,
                  isShaneLocation: true,
                  customIcon: r.customIcon ?? data.defaultIcon,
                };
              }),
            );
            geoJsonRef.current = geoJson;

            console.log('setting markers');
            const markers = await addLocationsToMap(geoJson, true, false, true);

            const bounds = new mapboxgl.LngLatBounds();

            console.log('adding marker listeners');
            markers?.forEach((marker) => {
              bounds.extend(marker.marker.getLngLat());
              setMarkerVisibility(marker, true);
              const feature = geoJson.features.find(
                (r: any) => r.properties.id === marker.id,
              );

              const onMarkerClick = (e: any) => {
                e.stopPropagation();
                if (map.current) {
                  if (
                    selectedFeature?.current &&
                    selectedFeature.current !== feature
                  ) {
                    const marker = markers?.find(
                      (m: any) =>
                        m.id === selectedFeature.current.properties.id,
                    )?.marker;

                    if (marker) {
                      marker.getPopup()?.remove();
                    }

                    selectedFeature.current = null;
                  }
                  const newPopup = new mapboxgl.Popup({ offset: 25 });

                  newPopup
                    //@ts-ignore
                    .setLngLat(feature.geometry.coordinates)
                    .setHTML(
                      '<h3 style="font-family: Rethink Sans;">' +
                        feature?.properties?.name +
                        '</h3>',
                    )
                    .addTo(map.current);

                  marker.marker.setPopup(newPopup);
                  if (selectedFeature?.current === feature) {
                    try {
                      if (feature?.properties?.externalId) {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_ext_location ' +
                            feature?.properties?.externalId,
                        );
                      } else if (feature?.properties?.rrId) {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_rr_location:' +
                            JSON.stringify({
                              id: feature?.properties?.rrId,
                              lat: feature?.properties.lat,
                              lng: feature?.properties.lng,
                            }),
                        );
                      } else if (typeof feature?.properties?.id === 'string') {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_mapbox_location ' + feature?.properties?.id,
                        );
                      } else {
                        //@ts-ignore
                        window.flutter_inappwebview.callHandler(
                          'fromWebApp',
                          'open_location ' + feature?.properties?.id,
                        );
                      }
                    } catch (e) {
                      // console.log("caz POPPING 1", feature?.properties);
                    }
                  }
                  selectedFeature.current = feature;
                }
              };

              const el = marker.marker.getElement();

              el.addEventListener('click', onMarkerClick);

              const cleanupListeners = () => {
                el.removeEventListener('click', onMarkerClick);
              };

              marker.marker.on('remove', () => {
                cleanupListeners();
              });
            });

            if (!map?.current) return;

            newMarkersRef.current = markers ?? [];

            console.log('setting bounds');
            map.current.fitBounds(bounds, {
              padding: 50,
              maxZoom: 15,
              minZoom: 4,
              animate: !skipLoadAnimation,
            });
          } catch (error) {
            console.error('Error fetching data:', error);
          }
        };

        fetchListData();
        console.log('setting load completed');
        setIsLoadCompleted(true);
      }

      if (isInit && (searchId === '' || searchId === null) && !isListMode) {
        console.log('preloading');
        setIsPreloadMode(true);

        try {
          //@ts-ignore
          window.flutter_inappwebview.callHandler(
            'fromWebApp',
            'preloading locations',
          );
        } catch (e) {}

        const preFetchData = async () => {
          try {
            let query: any = {};
            if (!map?.current) {
              throw Error('Map not found');
            }

            const { profile } = useAppState.getState();
            const { data } = await preloadMap({
              client: shaneClient,
              query: {
                ...query,
                gender: profile.gender ?? undefined,
                additionalGender: profile.additionalGender ?? undefined,
                orientation: profile.orientation ?? undefined,
                minLat: -90,
                minLng: -180,
                maxLat: 90,
                maxLng: 180,
              },
            });

            const tempResults = (data as any)?.data.map((d: any) => ({
              ...d,
              ratings: [
                {
                  who: 'All perspectives',
                  rating: d?.ratingAll ? parseFloat(d?.ratingAll) : '-',
                },
              ],
            }));

            //@ts-ignore
            const geoJson = searchResultsToGeoJson(tempResults);

            const { setPreloadedGeoJson } = useAppState.getState();
            setPreloadedGeoJson(geoJson);
            setTimeout(() => {
              if (!map?.current) return;
              map.current.panTo(map.current.getCenter());
            }, 500);
          } catch (error) {
            console.error('Error fetching data:', error);
          }
        };

        setTimeout(() => {
          preFetchData();
        }, 50);
      }

      //fly to location
      if (
        locId &&
        selectedFeature?.current !== locId &&
        (listId || searchId) &&
        locId !== '0'
      ) {
        console.log('flying to location');
        setTimeout(() => {
          const marker = newMarkersRef.current?.find(
            (m: any) => m.id === parseInt(locId),
          )?.marker;

          try {
            const feature = geoJsonRef.current.features.find(
              (r: any) => r.properties.id === parseInt(locId),
            );

            try {
              //@ts-ignore
              window.flutter_inappwebview.callHandler(
                'fromWebApp',
                'flying to location ' +
                  JSON.stringify(feature) +
                  ' - ' +
                  locId +
                  ' - ' +
                  JSON.stringify(geoJsonRef.current),
              );
            } catch (e) {
              // console.log(
              //   "caz",
              //   "flying to location " +
              //     JSON.stringify(feature) +
              //     " - " +
              //     locId +
              //     " - " +
              //     JSON.stringify(geoJsonRef.current)
              // );
            }

            //@ts-ignore
            selectedFeature.current = feature;

            if (map.current && marker && feature) {
              const newPopup = new mapboxgl.Popup({ offset: 25 });

              newPopup
                //@ts-ignore
                .setLngLat(feature.geometry.coordinates)
                .setHTML(
                  '<h3 style="font-family: Rethink Sans;">' +
                    feature?.properties?.name +
                    '</h3>',
                )
                .addTo(map.current);

              marker.setPopup(newPopup);

              map.current.stop();

              setTimeout(() => {
                if (!map?.current) return;
                map.current.flyTo({
                  center: feature.geometry.coordinates,
                  speed: 2,
                  zoom: 10,
                  essential: true,
                });
              }, 100);

              return;
            }
          } catch (e) {
            console.log('failed to fly', e);
          }
        }, 1000);
      }
    },
    [
      accessToken,
      addLocationsToMap,
      handleMove,
      map,
      newMarkersRef,
      searchResultsToGeoJson,
      selectedFeature,
      // setFlyingTo,
      setIsListMode,
      setIsLoadCompleted,
      setIsPreloadMode,
      setMarkerVisibility,
      shaneClient,
      isListMode,
    ],
  );

  const setMapReadOnlyStatus = (isReadOnly: boolean) => {
    if (!map?.current) return;
    //if r / !r, set map config
    if (isReadOnly) {
      map.current.dragPan.disable();
      map.current.scrollZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.boxZoom.disable();
      map.current.keyboard.disable();
      map.current.touchZoomRotate.disable();
      map.current.dragRotate.disable();
    } else {
      map.current.dragPan.enable();
      map.current.scrollZoom.enable();
      map.current.doubleClickZoom.enable();
      map.current.boxZoom.enable();
      map.current.keyboard.enable();
      map.current.touchZoomRotate.enable();
      map.current.dragRotate.enable();
    }
  };

  //@ts-ignore
  window.handleFlutterMessage = function (params: Record<string, unknown>) {
    console.log('Params from Flutter:', params);

    var p: any[] = [];

    Object.entries(params).forEach(([key, value]) => {
      p.push({ key, value: String(value) });
    });

    const listId = p.find((o: any) => o.key === 'l')?.value ?? null;
    const searchId = p.find((o: any) => o.key === 'se')?.value ?? null;
    const locationId = p.find((o: any) => o.key === 'loc')?.value ?? null;
    const usrLat = p.find((o: any) => o.key === 'lat')?.value ?? null;
    const usrLng = p.find((o: any) => o.key === 'lng')?.value ?? null;
    const skipLoadAnimation = p.find((o: any) => o.key === 'sk')?.value
      ? true
      : false;
    const skipBoundsChange = p.find((o: any) => o.key === 'bo')?.value
      ? true
      : false;
    const skipPreloading = p.find((o: any) => o.key === 'spre')?.value
      ? true
      : false;

    const viewportMinLat =
      p.find((o: any) => o.key === 'vminlat')?.value ?? null;
    const viewportMinLng =
      p.find((o: any) => o.key === 'vminlng')?.value ?? null;
    const viewportMaxLat =
      p.find((o: any) => o.key === 'vmaxlat')?.value ?? null;
    const viewportMaxLng =
      p.find((o: any) => o.key === 'vmaxlng')?.value ?? null;
    const isReadOnly =
      p.find((o: any) => o.key === 's')?.value === 'false' ? false : true;

    if (
      viewportMaxLat &&
      viewportMaxLng &&
      viewportMinLat &&
      viewportMinLng &&
      map.current
    ) {
      map.current.fitBounds(
        [viewportMinLng, viewportMinLat, viewportMaxLng, viewportMaxLat],
        {
          //padding: 50, // Add 50px padding on all sides
          essential: true, // For smooth transitions during initial load
        },
      );
    }

    applyParamChanges(
      listId,
      searchId,
      locationId,
      usrLat,
      usrLng,
      skipLoadAnimation,
      !skipPreloading,
      skipBoundsChange,
    );

    if (isReadOnly !== isReadOnlyRef.current) {
      setMapReadOnlyStatus(isReadOnly);
    }

    listIdRef.current = listId;
    searchIdRef.current = searchId;
    locationIdRef.current = locationId;
    usrLatRef.current = usrLat;
    usrLngRef.current = usrLng;
    skipLoadAnimationRef.current = skipLoadAnimation;
    // viewportLatRef.current = viewportLat;
    // viewportLngRef.current = viewportLng;
    // viewportZoomRef.current = viewportZoom;
    isReadOnlyRef.current = isReadOnly;
  };

  useEffect(() => {
    if (!accessToken || !isMapInit) {
      return;
    }

    if (hasDataInit.current) return;
    hasDataInit.current = true;

    const queryParams = new URLSearchParams(location.search);

    const listId = queryParams.get('l');
    const searchId = queryParams.get('se');
    var locId = queryParams.get('loc');
    const userLat = queryParams.get('lat');
    const userLng = queryParams.get('lng');
    const skipLoadAnimation = queryParams.get('sk') ? true : false;
    const skipBoundsChange = queryParams.get('bo') ? true : false;
    const skipPreloading = queryParams.get('spre') ? true : false;

    if (locId === '0') locId = null;

    const settingsString = localStorage.getItem(appSettingsLocalStorageKey);

    if (settingsString && settingsString !== '{}') {
      if (JSON.parse(settingsString)) {
        const showRatings = JSON.parse(settingsString)['Show ratings on map'];
        setShowRatingMarkers(showRatings);
      }
    }

    console.log('triggering applyparamchanges on load');
    applyParamChanges(
      listId,
      searchId,
      locId,
      userLat,
      userLng,
      skipLoadAnimation,
      !skipPreloading,
      skipBoundsChange,
    );

    moveBottomRightControls(listId ? 'list' : 'search');
  }, [location.search, accessToken, applyParamChanges, isMapInit, setShowRatingMarkers]);

  useEffect(() => {
    if (!popupContainerRef.current) {
      const container = document.createElement('div');
      popupContainerRef.current = container;
      popupContainerRef.current.setAttribute('id', 'popup-container');
      popupContainerRef.current.style.width = 'fit-content';
      popupContainerRef.current.style.paddingTop = '6px';
      popupContainerRef.current.style.paddingBottom = '6px';
      document.body.appendChild(popupContainerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!popupContainerRef?.current) return;
    popupContainerRef.current.style.height = 'fit-content';
    popupContainerRef.current.style.width = 'fit-content';
  }, [popupContainerRef]);

  //!SECTION

  //SECTION - set up popup behaviour
  const popUpHandlers = usePopUp({
    map,
    popup,
    popupContainerRef,
    mapContainer,
    isSmallScreen: true,
  });
  const {
    openPopUp, //, closePopUp
  } = useMemo(() => popUpHandlers, [popUpHandlers]);
  //!SECTION

  //SECTION - core map interactions - moving and clicking - declaring listeners which will be attached to a map instance

  const longPressMapInteractionHandlers = useLongPressMapInteraction({
    isSmallScreen: true,
    openPopUp,
    map,
    popup,
    popupContainerRef,
    mapContainer,
  });
  const { handleLongPressStart, handleLongPressEnd } = useMemo(
    () => longPressMapInteractionHandlers,
    [longPressMapInteractionHandlers],
  );
  //!SECTION

  //SECTION - initialise map
  const initMap = useMapInitialization({
    handleMove: debouncedHandleMoveEnd,
    handleLongPressStart,
    handleLongPressEnd,
    map,
    popupContainerRef,
    mapContainer,
    useClusters: new URLSearchParams(location.search).get('l') ? false : true,
  });

  const [mapContainerReady, setMapContainerReady] = useState(false);

  useEffect(() => {
    if (
      mapContainer?.current &&
      !mapContainerReady &&
      popupContainerRef?.current
    ) {
      setMapContainerReady(true);
    }
  }, [mapContainer, mapContainerReady, popupContainerRef]);

  useEffect(() => {
    if (mapContainerReady) {
      initMap(true);
    }
  }, [mapContainerReady, initMap]);

  //!SECTION

  //$Check user's location permission, prompt if necessary, set user's lat / lng
  // useEffect(() => {
  //   const handleClick = async () => {
  //     if (!map?.current) return;
  //     if (skipPermissionsCheck) {
  //       document.removeEventListener("click", handleClick);
  //       return;
  //     }

  //     try {
  //       writeToLog(["[App] Checking location permissions"]);
  //       setSkipPermissionsCheck(true);
  //       const loc = await checkPermissions();

  //       document.removeEventListener("click", handleClick);

  //       if (loc) {
  //         const { hasUserMovedMap } = useAppState.getState();
  //         if (hasUserMovedMap) return;

  //         setLat(loc.lat);
  //         setLng(loc.lng);
  //         setPermissionStatus(loc.state);
  //         setFlyingTo(true);
  //         map.current.flyTo({
  //           center: [loc.lng, loc.lat],
  //           zoom:
  //             loc.lat === defaultLat && loc.lng === defaultLng
  //               ? 3
  //               : map.current.getZoom() ?? 7,
  //           speed: 3,
  //           essential: true,
  //         });

  //         const handleMoveEnd = () => {
  //           if (!map?.current) return;
  //           setTimeout(() => {
  //             setFlyingTo(false);
  //           }, 400);
  //           map.current.off("moveend", handleMoveEnd);
  //         };

  //         map.current.on("moveend", handleMoveEnd);
  //       }
  //     } catch (e) {
  //       console.error(e);
  //       setLat(defaultLat);
  //       setLng(defaultLng);
  //       setPermissionStatus("denied");
  //     }
  //   };

  //   const memoizedHandleClick = handleClick;
  //   document.addEventListener("click", memoizedHandleClick);

  //   return () => {
  //     document.removeEventListener("click", memoizedHandleClick);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // const hasSetupRef = useRef<boolean>();

  // useEffect(() => {
  //   if (!isMapInit) return;
  //   if (hasSetupRef.current) return;

  //   const mapCurrent = map?.current;
  //   if (!mapCurrent) return;

  //   const handleMoveEnd = () => {
  //     if (!mapCurrent) return;
  //     setMovingAutomatically(false);
  //     mapCurrent.off?.("moveend", handleMoveEnd);
  //   };

  //   const watchId = navigator.geolocation.watchPosition(
  //     (position) => {
  //       writeToLog(["[App] watching position"]);
  //       if (!mapCurrent) return;

  //       const { latitude, longitude } = position.coords;

  //       if (userMarker) {
  //         userMarker.setLngLat([longitude, latitude]);
  //       } else {
  //         const el = document.createElement("div");
  //         el.className = "marker-user";

  //         const m = new mapboxgl.Marker(el)
  //           .setLngLat([longitude, latitude])
  //           .addTo(mapCurrent);

  //         setUserMarker(m);
  //       }

  //       const { autoCenter } = useAppState.getState();
  //       if (autoCenter) {
  //         setMovingAutomatically(true);
  //         mapCurrent.setCenter([longitude, latitude]);

  //         mapCurrent.on("moveend", handleMoveEnd);
  //       }
  //     },
  //     (error) => {
  //       console.error("Error watching position:", error);
  //     },
  //     {
  //       enableHighAccuracy: true,
  //       maximumAge: 10000,
  //       timeout: 5000,
  //     }
  //   );

  //   hasSetupRef.current = true;

  //   return () => {
  //     if (!mapCurrent) return; // ✅ Extra safety check

  //     navigator.geolocation.clearWatch(watchId);

  //     if (typeof mapCurrent.off === "function") {
  //       mapCurrent.off("moveend", handleMoveEnd);
  //     }
  //   };
  // }, [map, setMovingAutomatically, autoCenter, setUserMarker, userMarker, isMapInit]);

  // //force pan to user position when autocenter is turned on
  // useEffect(() => {
  //   if (autoCenter) {
  //     const run = async () => {
  //       const mapCurrent = map?.current;
  //       if (!mapCurrent) return;
  //       const loc = await requestLocation();

  //       const handleMoveEnd = () => {
  //         if (!mapCurrent) return;
  //         setMovingAutomatically(false);
  //         mapCurrent.off?.("moveend", handleMoveEnd);
  //       };

  //       setMovingAutomatically(true);
  //       mapCurrent.setCenter([loc.lng, loc.lat]);

  //       mapCurrent.on("moveend", handleMoveEnd);
  //     };

  //     run();
  //   }
  // }, [autoCenter, setMovingAutomatically]);

  return (
    <TamaguiProvider config={tamaguiConfig} defaultTheme="sun">
      <ToastContainer style={{ zIndex: 999999 }} />

      {/* //SECTION - dialogs */}
      <AppSettingsDialog
        // isOpen={isSettingsDialogOpen}
        onClose={() => {
          setIsSettingsDialogOpen(false);
        }}
        onProfileChange={async () => {}}
        isSmallScreen={true}
      />
      <HelpLinksDialog
        // isOpen={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
        isSmallScreen={true}
      />
      {/* //!SECTION  */}
      {/* //SECTION - core UI */}
      <div
        id="dad"
        style={{
          width: '100vw',
          height: '100vh',
          boxSizing: 'content-box',
          overflow: 'hidden',
        }}
      >
        {/* //NOTE desktop + mobile - pulsing submark overlay when running a search */}
        {/* <SearchingOverlay /> */}
        {/* //NOTE desktop and mobile - map container */}
        <MapContainer isSmallScreen={true} mapContainer={mapContainer} />
      </div>
      {/* //NOTE desktop - 'search this area' button */}
      {showSTA && !isListMode && !isPreloadMode && isLoadCompleted && (
        <SearchThisAreaDesktop
          isSmallScreen={true}
          popupContainerRef={popupContainerRef}
          map={map}
          updateMapWithSearchResults={updateMapWithSearchResults}
        />
      )}
      {/* {zoomToSearchRunning && <ZoomToSearchDots isSmallScreen={true} />} */}
      {/* //!SECTION */}
    </TamaguiProvider>
  );
});

export default App;
